import { Fragment, type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { useCurrentPageType, useTrackImpressionEventByIntersection } from "shared/hooks";
import type { PageType } from "shared/lib/page-type";
import { getAbsoluteUrl } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { pathname as livePathName } from "scmp-app/pages/live";
import type { latestContentsHomeV2Query$key } from "scmp-app/queries/__generated__/latestContentsHomeV2Query.graphql";

import {
  Arrow,
  Container,
  Divider,
  Item1,
  Item2,
  Item3,
  Item4,
  Item5,
  ItemsContainer,
  MoreLink,
  SectionTitleLink,
} from "./styles";

type Props = {
  className?: string;
  reference: latestContentsHomeV2Query$key;
};

export const HomeV2Latest: FunctionComponent<Props> = ({ className, reference }) => {
  const data = useFragment(
    graphql`
      fragment latestContentsHomeV2Query on Query
      @argumentDefinitions(
        latestExcludeArticleTypes: { type: "[String]" }
        latestExcludeSectionIds: { type: "[String]" }
        scmpPlusPaywallTypeIds: { type: "[String]" }
      ) {
        latestV2Contents: contents(
          articleExcludeInput: {
            articleTypeIds: $latestExcludeArticleTypes
            paywallTypeIds: $scmpPlusPaywallTypeIds
          }
          articleFilterInput: { sponsorTypes: [NONE] }
          contentTypes: [ARTICLE, GALLERY, VIDEO]
          exclude: { sectionIds: $latestExcludeSectionIds }
          first: 5
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) {
          edges {
            node {
              entityId
              ...homeLatestV2ContentItemContent
            }
          }
        }
      }
    `,
    reference,
  );

  const items = data.latestV2Contents?.edges ?? [];
  const listOfArticles = items.map(item => item.node.entityId).join(",");

  const currentPageType = useCurrentPageType() as
    | PageType.Homepage
    | PageType.Section
    | PageType.SubSection;

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles,
        page_type: currentPageType,
        widget_name: "latest",
      },
      subcategory: "widget",
    }),
    [currentPageType, listOfArticles],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const itemComponents = [Item1, Item2, Item3, Item4, Item5];

  if (items.length === 0) return null;

  return (
    <BaseLinkContextProvider customQueryParameters={{ module: tracking.module.Latest }}>
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <SectionTitleLink pathname={livePathName}>Latest</SectionTitleLink>
        <ItemsContainer>
          {items.map(({ node }, index) => {
            const Component = itemComponents[index];
            return (
              <Fragment key={node.entityId}>
                <Component
                  onClick={(entityId, urlAlias) => {
                    sendGA4Tracking({
                      action: "click",
                      category: "recirculation",
                      customized_parameters: {
                        article_id: entityId,
                        destination_url: getAbsoluteUrl(urlAlias),
                        page_type: currentPageType,
                        widget_name: "latest",
                      },
                      subcategory: "widget",
                    });
                  }}
                  reference={node}
                />
                {index === 1 && <Divider />}
              </Fragment>
            );
          })}
        </ItemsContainer>
        <MoreLink pathname={livePathName}>
          More Latest News
          <Arrow />
        </MoreLink>
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2Latest.displayName = "HomeV2Latest";
